import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Package, Truck, ShoppingBag, MapPin, TrendingDown, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { dealsAPI } from '../lib/api';
import ShareButton from '../components/ShareButton';

export default function DealsPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await dealsAPI.list();
        setDeals(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-24 pb-16">
        {/* HERO */}
        <section className="max-w-6xl mx-auto px-4 mb-12">
          <div className="text-center">
            <Badge className="bg-amber-500 hover:bg-amber-600 text-white mb-4">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Nouveau
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Chivo, sans-serif'}}>
              ISM Deals
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Achetez en métropole. Profitez de nos départs groupés vers votre destination.
            </p>
            <p className="text-base text-amber-700 font-semibold mt-4">
              Vous achetez. Nous regroupons. Vous économisez.
            </p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="max-w-6xl mx-auto px-4 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { icon: ShoppingBag, title: '1 — Choisissez', desc: 'Découvrez notre sélection adaptée aux départs groupés' },
              { icon: Package, title: '2 — Achetez', desc: 'Directement chez le marchand' },
              { icon: MapPin, title: '3 — Recevez', desc: 'Chez ISM Parcel avec votre code client' },
              { icon: Truck, title: '4 — Regroupez', desc: 'Nous consolidons sur palettes' },
              { icon: TrendingDown, title: '5 — Économisez', desc: 'Transport mutualisé, coût optimisé' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`deals-step-${i}`}>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                  <s.icon className="w-5 h-5 text-amber-700" />
                </div>
                <div className="font-bold text-slate-900 mb-1">{s.title}</div>
                <div className="text-xs text-slate-600 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* DEALS LIST */}
        <section className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-black text-slate-900 mb-6" style={{fontFamily: 'Chivo, sans-serif'}}>
            Opérations en cours
          </h2>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Aucun deal actif pour l&apos;instant. Revenez bientôt !</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {deals.map((d) => (
                <div
                  key={d.id}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-amber-400 hover:shadow-xl transition-all relative"
                  data-testid={`deal-card-${d.slug}`}
                >
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                    {d.status === 'open' && (
                      <Badge className="bg-green-500 hover:bg-green-600">Ouvert</Badge>
                    )}
                    {d.status === 'closed' && (
                      <Badge className="bg-slate-500 hover:bg-slate-600">Terminé</Badge>
                    )}
                    <ShareButton
                      variant="icon"
                      url={`${window.location.origin}/deals/${d.slug}`}
                      title={d.name}
                      message={`🔥 Départ groupé ISM Parcel vers ${d.destination?.name} — ${d.products_count || 0} produits sélectionnés. 👉 ${window.location.origin}/deals/${d.slug}`}
                      testId={`deal-share-${d.slug}`}
                    />
                  </div>
                  <Link to={`/deals/${d.slug}`} className="block">
                  <div className="h-32 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 relative flex items-center justify-center">
                    <div className="text-5xl">{d.destination?.flag || '🌍'}</div>
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-amber-700 font-bold uppercase tracking-wider mb-1">
                      {d.destination?.name || d.destination_code}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-700 transition-colors">
                      {d.name}
                    </h3>

                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <div className="text-lg font-black text-slate-900">{d.products_count || 0}</div>
                        <div className="text-xs text-slate-500">produits</div>
                      </div>
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <div className="text-lg font-black text-slate-900">{d.interests_count || 0}</div>
                        <div className="text-xs text-slate-500">👀 intéressés</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg px-3 py-2">
                        <div className="text-lg font-black text-amber-900">{d.declared_orders_count || 0}</div>
                        <div className="text-xs text-amber-700">🛒 déclarées</div>
                      </div>
                      <div className="bg-green-50 rounded-lg px-3 py-2">
                        <div className="text-lg font-black text-green-900">{d.received_orders_count || 0}</div>
                        <div className="text-xs text-green-700">📦 reçues</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                      <span>🪵 {d.pallets_count || 0} palette(s) en remplissage</span>
                      {d.close_at && (
                        <span>Clôture : {new Date(d.close_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                      )}
                    </div>

                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid={`deal-open-${d.slug}`}>
                      Découvrir les produits →
                    </Button>
                  </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}
